import React, { useMemo } from 'react';
import DsButton from '../DsButton';
import EventFlyerMedia from '../EventFlyerMedia';
import EventAssigneeAvatars from './EventAssigneeAvatars';
import {
  buildTitleLines,
  clampPercent,
  formatTime,
  isPastEvent,
  resolveDateParts,
} from './EventListCard';

const EventListRow = ({
  navigationMenu = /** @type {import('react').ReactNode} */ (null),
  event,
  imgSrc,
  imgAlt,
  isLandscape,
  onFlyerLoad,
  onFlyerError,
  onFlyerDisplayTimeout,
  flyerReveal,
  flyerDelayMs = 0,
  flyerDisplayTimeoutMs = 0,
  flyerLoading = 'lazy',
  flyerFetchPriority,
  onClick,
  isToday,
  todayLabel,
  isCancelled,
  venueDisplay,
  badges,
  infoIcons,
  progressItems = [],
  onChatClick,
  unreadCount = 0,
  chatLabel = 'チャット',
  placeholderDesignKey = '',
  placeholderColorKey = '',
  noticeLabel = '',
}) => {
  const dateParts = useMemo(() => resolveDateParts(event?.event_date), [event?.event_date]);
  const titleLines = useMemo(
    () => buildTitleLines(event?.name, event?.performance_type, false),
    [event?.name, event?.performance_type]
  );
  const openTime = formatTime(event?.open_time);
  const startTime = formatTime(event?.start_time);
  const showProgress = Array.isArray(progressItems) && progressItems.length > 0;
  const showAside = showProgress || Boolean(onChatClick);
  const isPast = useMemo(() => isPastEvent(event), [event]);
  const unread = Number(unreadCount) || 0;
  const shouldFadeFlyer = typeof flyerReveal === 'boolean';
  const flyerMediaClassName = shouldFadeFlyer
    ? `event-flyer-media--fade${flyerReveal ? ' is-visible' : ''}`
    : '';
  const flyerMediaStyle = shouldFadeFlyer && flyerDelayMs
    ? { transitionDelay: `${flyerDelayMs}ms` }
    : undefined;
  const flyerClassName = `event-list-row__image${isLandscape ? ' is-landscape' : ''}`;
  const mobileChatButtonClassName = 'event-list-row__chat-btn event-list-row__chat-btn--mobile';
  const desktopChatButtonClassName = 'event-list-row__chat-btn event-list-row__chat-btn--desktop';

  const handleChatClick = (e) => {
    if (!onChatClick) return;
    e?.preventDefault?.();
    e?.stopPropagation?.();
    onChatClick(e);
  };

  return (
    <div data-selection-safe-navigation
      className={`event-list-row${isToday ? ' is-today' : ''}${isCancelled ? ' is-cancelled' : ''}`}
      onClick={onClick}
    >
      {navigationMenu}
      {isToday && todayLabel && (
        <span className="today-badge" aria-label={todayLabel}>
          {todayLabel}
        </span>
      )}

      {onChatClick && !event?.partner_only && (
        <DsButton
          variant="ghost"
          icon
          className={mobileChatButtonClassName}
          onClick={handleChatClick}
          aria-label={chatLabel}
          title={chatLabel}
        >
          <i className="fa-solid fa-comments" aria-hidden="true"></i>
          {unread > 0 && (
            <span className="unread-badge" aria-label={`未読 ${unread} 件`}>
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </DsButton>
      )}

      <div className="event-list-row__content">
        <div className="event-list-row__main">
          <div
            className={`event-list-row__flyer${flyerMediaClassName ? ` ${flyerMediaClassName}` : ''}`}
            style={flyerMediaStyle}
          >
            <EventFlyerMedia
              event={event}
              imageUrl={imgSrc}
              imageAlt={imgAlt || 'Flyer'}
              sizeVariant="small"
              borderRadius={8}
              placeholderClassName="event-list-row__placeholder"
              imageClassName={flyerClassName}
              hasImage={Boolean(imgSrc)}
              visualMode={imgSrc && isLandscape ? 'solid' : 'decorated'}
              placeholderDesignKey={placeholderDesignKey}
              placeholderColorKey={placeholderColorKey}
              onImageLoad={(target) => onFlyerLoad && onFlyerLoad(target)}
              onDisplayTimeout={() => onFlyerDisplayTimeout && onFlyerDisplayTimeout()}
              displayTimeoutMs={flyerDisplayTimeoutMs}
              loading={flyerLoading}
              fetchPriority={flyerFetchPriority}
              onImageError={(target) => {
                if (onFlyerError) onFlyerError(target);
                target.onerror = null;
                target.style.display = 'none';
              }}
            />
          </div>

          {dateParts && (
            <div
              className="event-list-row__date"
              aria-label={`${dateParts.yearLabel ? `${dateParts.yearLabel}/` : ''}${dateParts.monthLabel}/${dateParts.dayLabel} ${dateParts.weekdayLabel}`.trim()}
            >
              <div className="event-card-date">
                {dateParts.yearLabel && (
                  <span className="event-card-date-year">{dateParts.yearLabel}</span>
                )}
                <div className="event-card-date-row">
                  <span className="event-card-date-month">{dateParts.monthLabel}</span>
                  <span className="event-card-date-slash">/</span>
                  <span className="event-card-date-day">{dateParts.dayLabel}</span>
                </div>
                <span className={`event-card-date-meta ${dateParts.weekdayClass}`}>
                  {dateParts.weekdayLabel}
                </span>
              </div>
            </div>
          )}

          <div className={`event-list-row__info${onChatClick ? ' event-list-row__info--has-chat' : ''}`}>
            {(badges || infoIcons) && (
              <div className="event-list-row__meta-badges">
                {badges}
                {infoIcons}
              </div>
            )}
            <div className="event-list-row__title-block">
              <div className={`event-list-row__title${isCancelled ? ' is-cancelled' : ''}`}>
                {titleLines.line1 && (
                  <span className={`event-list-row__title-line${noticeLabel ? ' has-notice' : ''}`}>
                    {noticeLabel ? (
                      <span className="event-list-row__notice-mark" aria-label={noticeLabel} title={noticeLabel}>
                        <i className="fa-solid fa-circle-exclamation" aria-hidden="true" />
                      </span>
                    ) : null}
                    {event?.partner_shared && <span>パートナー共有　</span>}{titleLines.line1}
                  </span>
                )}
                {titleLines.line2 && (
                  <span
                    className={`event-list-row__title-line${
                      titleLines.line2Type === 'performance' ? ' event-list-row__title-line--performance' : ''
                    }`}
                  >
                    {titleLines.line2}
                  </span>
                )}
              </div>
            </div>

            {(openTime || startTime) && (
              <div className="event-list-row__time">
                {openTime && (
                  <span className="event-list-row__time-item">
                    <span className="event-list-row__time-label">OPEN</span>
                    <span className="event-list-row__time-value">{openTime}</span>
                  </span>
                )}
                {openTime && startTime && <span className="event-list-row__time-sep">/</span>}
                {startTime && (
                  <span className="event-list-row__time-item">
                    <span className="event-list-row__time-label">START</span>
                    <span className="event-list-row__time-value">{startTime}</span>
                  </span>
                )}
              </div>
            )}

            {(venueDisplay?.name || venueDisplay?.link) && (
              <div className="event-list-row__venue">
                <i className="fa-solid fa-location-dot event-list-row__venue-icon" aria-hidden="true"></i>
                {venueDisplay?.link ? (
                  <a
                    className="event-list-row__venue-link"
                    href={venueDisplay.link}
                    onClick={(e) => e.stopPropagation()}
                    title={venueDisplay?.name || undefined}
                  >
                    {venueDisplay?.name}
                  </a>
                ) : (
                  <span className="event-list-row__venue-name" title={venueDisplay?.name || undefined}>
                    {venueDisplay?.name}
                  </span>
                )}
              </div>
            )}
            <EventAssigneeAvatars
              assignees={event?.assigned_admins || event?.assignedAdmins}
              className="event-list-row__assignees"
            />
          </div>
        </div>

        {showAside && (
          <div className={`event-list-row__aside${showProgress ? '' : ' event-list-row__aside--no-progress'}`}>
            {showProgress && (
              <div className="event-list-row__progress" aria-label="Progress">
                {progressItems.map((item) => {
                  const percent = clampPercent(item?.percent);
                  return (
                    <div key={item?.key || item?.label} className="event-list-row__progress-item">
                      <div className="event-list-row__progress-labels">
                        <span className="event-list-row__progress-label">
                          <i
                            className={`fa-solid ${
                              item?.tone === 'ticket'
                                ? 'fa-ticket'
                                : item?.tone === 'task'
                                ? 'fa-list-check'
                                : 'fa-user'
                            } event-card-progress-icon event-card-progress-icon--${item?.tone || 'default'}`}
                            aria-hidden="true"
                          ></i>
                          <span className="event-list-row__progress-text">{item?.label}</span>
                        </span>
                        <span
                          className={`event-card-progress-value event-card-progress-value--${item?.tone || 'default'}`}
                        >
                          {item?.valueLabel || `${percent}%`}
                        </span>
                      </div>
                      <div
                        className="event-card-progress-track"
                        aria-label={`${item?.label || 'Progress'} ${percent}%`}
                      >
                        <div
                          className={`event-card-progress-fill event-card-progress-fill--${item?.tone || 'default'}${
                            isPast ? ' is-static' : ''
                          }`}
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {onChatClick && !event?.partner_only && (
              <div className="event-list-row__chat">
                <DsButton
                  variant="ghost"
                  className={desktopChatButtonClassName}
                  onClick={handleChatClick}
                  aria-label={chatLabel}
                >
                  <i className="fa-solid fa-comments" aria-hidden="true"></i>
                  <span className="event-list-row__chat-label">{chatLabel}</span>
                  {unread > 0 && (
                    <span className="unread-badge" aria-label={`未読 ${unread} 件`}>
                      {unread > 99 ? '99+' : unread}
                    </span>
                  )}
                </DsButton>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventListRow;
